import React from "react";
import styled from "styled-components";

const Page = styled.div`
  --bg: #0a0a12;
  --panel: #101018;
  --surface: #16161f;
  --text: #f5f6fa;
  --dim: #a2a6b4;
  --faint: #6c7080;
  --accent: #8785f8;
  --border: rgba(255, 255, 255, 0.08);

  min-height: 100vh;
  background: var(--bg);
  color: var(--text);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica,
    Arial, sans-serif;
  line-height: 1.65;
  -webkit-font-smoothing: antialiased;

  * {
    box-sizing: border-box;
  }
`;

const Wrap = styled.div`
  max-width: 760px;
  margin: 0 auto;
  padding: 40px 22px 80px;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  gap: 14px;
  padding-bottom: 22px;
  margin-bottom: 28px;
  border-bottom: 1px solid var(--border);
`;

const Badge = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 13px;
  background: linear-gradient(150deg, #5b5fe9, #7c6bf2);
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;

  svg {
    width: 28px;
    height: 28px;
  }
`;

const Brand = styled.div`
  font-size: 24px;
  font-weight: 800;
  letter-spacing: 0.2px;

  span {
    color: var(--accent);
  }
`;

const Title = styled.h1`
  font-size: 30px;
  margin: 6px 0 4px;
`;

const Updated = styled.p`
  color: var(--faint);
  font-size: 14px;
  margin-bottom: 28px;
`;

const Section = styled.h2`
  font-size: 19px;
  margin: 34px 0 10px;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 10px;

  &::before {
    content: "";
    width: 4px;
    height: 18px;
    background: var(--accent);
    border-radius: 2px;
    flex: none;
  }
`;

const Body = styled.div`
  p,
  li {
    color: var(--dim);
    font-size: 16px;
  }

  ul {
    padding-left: 20px;
  }

  li {
    margin-bottom: 10px;
  }

  strong {
    color: var(--text);
    font-weight: 600;
  }

  a {
    color: var(--accent);
  }
`;

const Card = styled.div`
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 16px 18px;
  color: var(--dim);
  font-size: 15px;
`;

const Note = styled.div`
  background: var(--surface);
  border-left: 3px solid var(--accent);
  border-radius: 8px;
  padding: 14px 16px;
  margin: 16px 0;
  color: var(--dim);
  font-size: 15px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 12px 0;

  td {
    padding: 10px 12px;
    border-bottom: 1px solid var(--border);
    font-size: 15px;
    color: var(--dim);
    vertical-align: top;
  }

  td:first-child {
    color: var(--text);
    white-space: nowrap;
    width: 38%;
  }
`;

const Footer = styled.footer`
  margin-top: 48px;
  padding-top: 22px;
  border-top: 1px solid var(--border);
  color: var(--faint);
  font-size: 14px;

  a {
    color: var(--accent);
  }
`;

const Mark = () => (
  <svg viewBox="0 0 512 512" role="img" aria-label="AI Utility Hub">
    <g
      fill="none"
      stroke="#FFFFFF"
      strokeWidth="34"
      strokeLinecap="butt"
      strokeLinejoin="miter"
      strokeMiterlimit="1.2"
    >
      <path d="M78 344 L128 184 L178 344" />
      <path d="M99 276 L157 276" />
      <path d="M222 184 L222 292 A42 42 0 0 0 306 292 L306 184" />
      <path d="M352 184 L352 344" />
      <path d="M430 184 L430 344" />
      <path d="M352 276 L430 276" />
    </g>
  </svg>
);

const AiUtilityHub = () => {
  return (
    <Page>
      <Wrap>
        <Header>
          <Badge>
            <Mark />
          </Badge>
          <Brand>
            AI Utility <span>Hub</span>
          </Brand>
        </Header>

        <Title>Privacy Policy</Title>
        <Updated>Last updated: 8 September 2026</Updated>

        <Body>
          <p>
            AI Utility Hub (“we”, “us”, “the app”) is a collection of AI writing
            tools published by Nimions. This policy explains what the app
            collects, who it is shared with, and the choices you have. Please
            read the section on{" "}
            <strong>what you type into the tools</strong> — it is the most
            important part of this policy.
          </p>

          <Section as="h2">Information we collect</Section>
          <ul>
            <li>
              <strong>Account information.</strong> The app signs you in
              automatically with an <strong>anonymous account</strong> so you can
              start straight away — this creates an account identifier but no
              name or email. If you choose to sign in with email, Google or
              Apple, we then store your <strong>email address</strong> and, where
              the provider supplies it, your <strong>name</strong>. Sign-in is
              handled by Google Firebase Authentication.
            </li>
            <li>
              <strong>What you type, and what the AI writes back.</strong> Each
              generation is saved to your history so you can find it again. That
              record contains the <strong>text you entered</strong>, the{" "}
              <strong>generated result</strong>, which tool you used, the credits
              it cost and the time. It is stored on your device and, when you are
              signed in, in your private area of our Google Firestore database.
            </li>
            <li>
              <strong>Device identifier.</strong> On Android we read the{" "}
              <strong>Android ID</strong> and store it so the one-time grant of
              20 free credits is issued <strong>once per device</strong> rather
              than once per account. It is used for that purpose only — not for
              advertising or profiling.
            </li>
            <li>
              <strong>Usage and account state.</strong> Your credit balance,
              number of generations, Pro status and last-active time are stored
              on your account record. We also keep{" "}
              <strong>aggregate daily totals</strong> (generations, tokens and
              cost) used to run and monitor the service.
            </li>
            <li>
              <strong>Analytics and crash reports.</strong> Firebase Analytics
              records screen views and in-app events, tagged with your account
              identifier and whether you are a Pro subscriber. Firebase
              Crashlytics records crash and error diagnostics.
            </li>
            <li>
              <strong>Notifications.</strong> If you allow notifications, Firebase
              Cloud Messaging issues a device token and subscribes it to our
              announcements topic.
            </li>
            <li>
              <strong>Advertising identifier.</strong> Free use is supported by{" "}
              <strong>Google AdMob</strong> banner and rewarded ads, which may use
              your device’s advertising ID. Pro subscribers do not see ads.
            </li>
          </ul>

          <Section as="h2">Your text is processed by AI providers</Section>
          <Note>
            When you tap Generate, the text you entered is sent{" "}
            <strong>directly from your device</strong> to the AI provider
            configured for that tool. Depending on that configuration this is one
            of <strong>Anthropic</strong>, <strong>OpenAI</strong>,{" "}
            <strong>OpenRouter</strong> or <strong>Google (Gemini)</strong>. Your
            text is handled under that provider’s own privacy policy and terms
            while they process it. Please do not enter passwords, payment card
            numbers, government identifiers, health records or anyone else’s
            confidential information.
          </Note>

          <Section as="h2">How we use your information</Section>
          <p>
            To generate the content you ask for, keep your history and saved items
            available across your devices, apply your credit balance and Pro
            entitlement, issue the free allowance fairly, deliver and measure ads
            that keep the app free, diagnose crashes, and understand which tools
            are used so we can improve them.
          </p>

          <Section as="h2">Sharing &amp; third parties</Section>
          <p>
            We do not sell your personal data and we do not use your generated
            content for advertising. Your data is processed by these services:
          </p>
          <Table>
            <tbody>
              <tr>
                <td>Google Firebase</td>
                <td>
                  Authentication, Firestore database, Analytics, Crashlytics and
                  Cloud Messaging
                </td>
              </tr>
              <tr>
                <td>Anthropic, OpenAI, OpenRouter, Google&nbsp;Gemini</td>
                <td>
                  Generating text from what you type — whichever provider is
                  configured for the tool you use
                </td>
              </tr>
              <tr>
                <td>Google AdMob</td>
                <td>Banner and rewarded advertising, and its measurement</td>
              </tr>
              <tr>
                <td>Google Play Billing</td>
                <td>
                  Processing and verifying Pro subscriptions. We receive a
                  purchase token and your subscription status — never your card
                  details
                </td>
              </tr>
            </tbody>
          </Table>

          <Section as="h2">Advertising choices</Section>
          <p>
            In the EEA and UK we ask for your consent before serving personalised
            ads, using Google’s User Messaging Platform. You can reopen that
            choice at any time from <strong>Settings → Ad privacy options</strong>
            . Subscribing to Pro removes ads entirely.
          </p>

          <Section as="h2">Data retention &amp; deletion</Section>
          <p>
            <strong>On your device.</strong> Settings → Delete account clears
            everything the app has stored locally — history, saved tools, credits
            and your session — and signs you out. Uninstalling the app or clearing
            its data has the same effect.
          </p>
          <Note>
            <strong>Please note:</strong> that in-app action clears local data
            only. It does <strong>not</strong> delete the copy of your account
            record and generation history held in our database. To have those
            deleted, email us at the address below from the address on your
            account, or include your account identifier. We will delete them
            within <strong>30 days</strong> and confirm when it is done.
          </Note>
          <p>
            Aggregate daily totals contain no personal identifiers and are
            retained as service records.
          </p>

          <Section as="h2">Children</Section>
          <p>
            AI Utility Hub is not directed to children under 13 and we do not
            knowingly collect data from them. If you believe a child has provided
            us with personal data, contact us and we will delete it.
          </p>

          <Section as="h2">Security</Section>
          <p>
            All data sent by the app travels over HTTPS. Sign-in is handled by
            Google Firebase, and database rules restrict each account to its own
            records. No method of transmission or storage is completely secure,
            so we cannot guarantee absolute security.
          </p>

          <Section as="h2">Your rights</Section>
          <p>
            Depending on where you live, you may have the right to access,
            correct, export or delete the personal data we hold about you, and to
            object to or restrict certain processing. Contact us using the details
            below and we will respond within 30 days.
          </p>

          <Section as="h2">Changes to this policy</Section>
          <p>
            We may update this policy from time to time. Material changes will be
            reflected by the “Last updated” date above.
          </p>

          <Section as="h2">Contact us</Section>
          <Card>
            Questions, data access or deletion requests:
            <br />
            <a href="mailto:hello@nimions.com">hello@nimions.com</a>
            <br />
            <a href="tel:+8801764928732">+880 1764 928732</a>
          </Card>
        </Body>

        <Footer>
          © 2026 Nimions. This policy applies to the AI Utility Hub mobile
          application (package <strong>com.nimions.utilityhub</strong>). See also
          our <a href="/terms/ai-utility-hub">Terms &amp; Conditions</a>.
        </Footer>
      </Wrap>
    </Page>
  );
};

export default AiUtilityHub;
